import { api } from "./axiosClient";
import type {
  ClauseApprovalEntry,
  ClauseAuditRow,
  ClauseCreateRequest,
  ClauseDetail,
  ClauseEditRequest,
  ClauseLibraryMeta,
  ClauseMatch,
  ClauseSearchRequest,
  ClauseSearchResponse,
  ClauseUsageEntry,
  ClauseVersion,
  RecordClauseUsageRequest,
} from "../types/clauseLibrary.types";

const base = "/clauselibrary";

const post = async <T>(url: string, body: unknown): Promise<T> =>
  (await api.post<T>(`${base}${url}`, body)).data;

const get = async <T>(url: string): Promise<T> =>
  (await api.get<T>(`${base}${url}`)).data;

/** Every change carries the clause's version so two people cannot overwrite each other. */
interface Versioned {
  id: string;
  expectedVersion: number;
}

export const clauseLibraryApi = {
  search: (req: ClauseSearchRequest) => post<ClauseSearchResponse>("/search", req),
  get: (id: string) => get<ClauseDetail>(`/get/${id}`),
  meta: () => get<ClauseLibraryMeta>("/meta"),

  create: (req: ClauseCreateRequest) => post<{ id: string }>("/create", req),
  edit: (req: ClauseEditRequest) => post<ClauseDetail>("/edit", req),
  revise: (req: Versioned & { changeSummary?: string | null }) =>
    post<ClauseDetail>("/revise", req),

  submit: (req: Versioned & { approverIds: string[]; comment?: string | null }) =>
    post<ClauseDetail>("/submit", req),
  withdraw: (req: Versioned & { comment?: string | null }) =>
    post<ClauseDetail>("/withdraw", req),
  approve: (req: Versioned & { comment?: string | null }) =>
    post<ClauseDetail>("/approve", req),
  requestChanges: (req: Versioned & { comment: string }) =>
    post<ClauseDetail>("/requestchanges", req),

  retire: (req: Versioned & { reason: string }) => post<ClauseDetail>("/retire", req),
  reinstate: (req: Versioned & { reason?: string | null }) =>
    post<ClauseDetail>("/reinstate", req),
  /** Soft delete; the reason is required and kept on the audit trail. */
  remove: (id: string, reason: string) => post<unknown>("/remove", { id, reason }),

  versions: (id: string) => get<ClauseVersion[]>(`/versions/${id}`),
  version: (versionId: string) => get<ClauseVersion>(`/version/${versionId}`),
  approvals: (id: string) => get<ClauseApprovalEntry[]>(`/approvals/${id}`),
  usage: (id: string) => get<ClauseUsageEntry[]>(`/usage/${id}`),
  auditLog: (id: string) => get<ClauseAuditRow[]>(`/auditlog/${id}`),

  recordUsage: (req: RecordClauseUsageRequest) => post<unknown>("/recordusage", req),
  /** Published clauses of this organisation whose wording is closest to the text. */
  match: (text: string, top = 3) => post<ClauseMatch[]>("/match", { text, top }),
};
